// `facets.js` expects a global `debounce()` helper (provided by `global.js` in most themes).
// Some collection templates load `facets.js` before `global.js`, which causes `ReferenceError: debounce is not defined`.
const debounceFn =
  typeof debounce === "function"
    ? debounce
    : function (fn, wait) {
        let t;
        return function (...args) {
          const ctx = this;
          clearTimeout(t);
          t = setTimeout(() => fn.apply(ctx, args), wait);
        };
      };

class FacetFiltersForm extends HTMLElement {
  static getFilterForm() {
    return document.getElementById("FacetFiltersForm");
  }

  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onSubmitHandler = this.onSubmitHandler.bind(this);

    this.debouncedOnSubmit = debounceFn((event) => {
      this.onSubmitHandler(event);
    }, 300);

    const filterForm = FacetFiltersForm.getFilterForm() || this.querySelector("form");
    if (filterForm) {
      filterForm.addEventListener("change", (event) => {
        if (event.target.type === "checkbox") {
          this.onSubmitHandler(event);
        }
      });
      filterForm.addEventListener("input", (event) => {
        if (event.target.type === "number" || event.target.type === "text") {
          this.debouncedOnSubmit(event);
        }
      });
    }

    if (window.innerWidth > 767) {
      document.body.addEventListener(
        "sortchange",
        function (e) {
          const form = FacetFiltersForm.getFilterForm();
          if (!form) return;
          const formData = new FormData(form);
          const searchParams = new URLSearchParams(formData).toString();
          FacetFiltersForm.renderPage(searchParams, e);
        },
        false
      );
    } else {
      const objOld = this;
      document.body.addEventListener(
        "sortchange",
        function (e) {
          const form = FacetFiltersForm.getFilterForm();
          if (!form) return;
          const sortSelect = form.querySelector("#SortBy");
          const objSelectTitle =
            sortSelect && sortSelect.selectedOptions[0]
              ? sortSelect.selectedOptions[0].text
              : "";
          const objOldContainer = objOld.querySelector(".mobile-toggle-facets--sort");
          if (objOldContainer) objOldContainer.innerHTML = objSelectTitle;
          const formData = new FormData(form);
          const searchParams = new URLSearchParams(formData).toString();
          FacetFiltersForm.renderPage(searchParams, e);
        },
        false
      );
    }

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      let searchParams = event.state ? event.state.searchParams : null;
      if (searchParams === undefined || searchParams === null) {
        searchParams = window.location.search.slice(1);
      }
      searchParams = FacetFiltersForm.stripCollectionRestoreParams(searchParams);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static stripCollectionRestoreParams(searchString) {
    if (!searchString) return "";
    const params = new URLSearchParams(searchString);
    params.delete("pagination__more");
    params.delete("selectedProductId");
    return params.toString();
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static parseSectionHtml(html) {
    return new DOMParser().parseFromString(html, "text/html");
  }

  static buildSectionUrl(sectionId, searchParams) {
    const params = new URLSearchParams(searchParams);
    params.set("section_id", sectionId);
    return `${window.location.pathname}?${params.toString()}`;
  }

  static setLoadingState(isLoading) {
    const grid = document.getElementById("ProductGridContainer");
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById("ProductCountDesktop");

    if (grid) grid.classList.toggle("loading", isLoading);
    if (countContainer) countContainer.classList.toggle("loading", isLoading);
    if (countContainerDesktop) countContainerDesktop.classList.toggle("loading", isLoading);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    if (searchParams === FacetFiltersForm.searchParamsPrev && FacetFiltersForm.activeFetch) {
      return;
    }

    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    FacetFiltersForm.setLoadingState(true);

    sections.forEach((section) => {
      const url = FacetFiltersForm.buildSectionUrl(section.section, searchParams);
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    if (FacetFiltersForm.activeFetch) {
      FacetFiltersForm.activeFetch.abort();
    }

    const controller = new AbortController();
    FacetFiltersForm.activeFetch = controller;

    fetch(url, { signal: controller.signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (controller.signal.aborted) return;

        FacetFiltersForm.activeFetch = null;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData.slice(-7),
          { html: responseText, url },
        ];
        FacetFiltersForm.renderSection(responseText, event);
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        FacetFiltersForm.activeFetch = null;
        FacetFiltersForm.setLoadingState(false);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event);
  }

  static renderSection(html, event) {
    const parsedHTML =
      typeof html === "string" ? FacetFiltersForm.parseSectionHtml(html) : html;

    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    FacetFiltersForm.setLoadingState(false);
    requestAnimationFrame(() => FacetFiltersForm.hookWishlist());
  }

  static hookWishlist() {
    if (typeof window.fetchCallbackFn === "function") {
      window._swat.fetchWrtEventTypeET(function (r) {
        window.fetchCallbackFn(r);
      });
    }
  }

  static renderProductGridContainer(parsedHTML) {
    if (window.CollectionPageCache) {
      window.CollectionPageCache.clear();
    }

    const sourceGrid = parsedHTML.getElementById("ProductGridContainer");
    const targetGrid = document.getElementById("ProductGridContainer");
    if (!sourceGrid || !targetGrid) return;

    targetGrid.innerHTML = sourceGrid.innerHTML;
  }

  static renderProductCount(parsedHTML) {
    const countElement = parsedHTML.getElementById("ProductCount");
    let count = "";
    if (countElement) {
      count = countElement.innerHTML;
    }

    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");

    if (container) {
      container.innerHTML = count;
      if (containerDesktop) {
        containerDesktop.innerHTML = count;
      }
    }
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter"
    );
    const activeFilter = event?.target?.closest?.(".js-filter");
    const matchesIndex = (element) =>
      activeFilter ? element.dataset.index === activeFilter.dataset.index : false;
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`
      );
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && activeFilter) {
      FacetFiltersForm.renderCounts(countsToRender, activeFilter);
    }
  }

  static renderActiveFacets(parsedHTML) {
    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = parsedHTML.querySelector(selector);
      const target = document.querySelector(selector);
      if (!activeFacetsElement || !target) return;
      target.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {}

  static renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML =
        source.querySelector(".facets__selected").outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    const productGrid = document.getElementById("product-grid");
    if (productGrid?.dataset?.id) {
      return [{ section: productGrid.dataset.id }];
    }

    const gridContainer = document.getElementById("ProductGridContainer");
    if (gridContainer?.dataset?.sectionId) {
      return [{ section: gridContainer.dataset.sectionId }];
    }

    const sectionEl = document.querySelector(
      ".collection-grid-section, .main-staff-collection"
    );
    if (sectionEl?.id?.startsWith("shopify-section-")) {
      return [{ section: sectionEl.id.replace("shopify-section-", "") }];
    }

    return [{ section: sectionEl?.dataset?.sectionId }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = FacetFiltersForm.getFilterForm();
    if (!form) return;
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.activeFetch = null;
FacetFiltersForm.searchParamsInitial = FacetFiltersForm.stripCollectionRestoreParams(
  window.location.search.slice(1)
);
FacetFiltersForm.searchParamsPrev = FacetFiltersForm.searchParamsInitial;
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) =>
      element.addEventListener("change", this.onRangeChange.bind(this))
    );

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector("a").addEventListener("click", (event) => {
      event.preventDefault();
      const form =
        this.closest("facet-filters-form") ||
        document.querySelector("facet-filters-form");
      form.onActiveFilterClick(event);
    });
  }
}

customElements.define("facet-remove", FacetRemove);
